/**
 * Secure Token Service
 * 
 * Provides secure token storage using HttpOnly cookies instead of localStorage.
 * Includes:
 * - Token storage in HttpOnly cookies
 * - Token retrieval from cookies or fallback to localStorage
 * - Token rotation and refresh mechanisms
 * - Backward compatibility with localStorage
 * 
 * This service enhances security by protecting authentication tokens
 * from XSS attacks while maintaining compatibility with existing code.
 */

// Cookie configuration
export interface CookieConfig {
  name: string;
  path: string;
  domain?: string;
  maxAge: number; // seconds
  secure: boolean;
  sameSite: 'strict' | 'lax' | 'none';
  httpOnly: boolean;
}

// Token storage configuration
export interface TokenStorageConfig {
  authTokenCookie: CookieConfig;
  refreshTokenCookie: CookieConfig;
  tenant_idCookie: CookieConfig;
  userDataCookie: CookieConfig;
  useSecureStorage: boolean;
  useLocalStorageFallback: boolean;
}

// Default token storage configuration
export const DEFAULT_TOKEN_STORAGE_CONFIG: TokenStorageConfig = {
  authTokenCookie: {
    name: 'astrapeak_auth_token',
    path: '/',
    maxAge: 1800, // 30 minutes (matches backend ACCESS_TOKEN_EXPIRE_MINUTES)
    secure: true,
    sameSite: 'strict',
    httpOnly: false // NOTE: HttpOnly cannot be set via document.cookie; requires Set-Cookie header from server
  },
  refreshTokenCookie: {
    name: 'astrapeak_refresh_token', // Standardized key name
    path: '/',
    maxAge: 604800, // 7 days (matches backend REFRESH_TOKEN_EXPIRE_DAYS)
    secure: true,
    sameSite: 'strict',
    httpOnly: false // NOTE: HttpOnly cannot be set via document.cookie; requires Set-Cookie header from server
  },
  tenant_idCookie: {
    name: 'tenant_id',
    path: '/',
    maxAge: 604800, // 7 days
    secure: true,
    sameSite: 'strict',
    httpOnly: false // Accessible to JavaScript for tenant context
  },
  userDataCookie: {
    name: 'user_data',
    path: '/',
    maxAge: 604800, // 7 days
    secure: true,
    sameSite: 'strict',
    httpOnly: false // Accessible to JavaScript for user context
  },
  useSecureStorage: true,
  useLocalStorageFallback: true // For backward compatibility
};

/**
 * Set a cookie with the specified configuration
 * @param name - Cookie name
 * @param value - Cookie value
 * @param options - Cookie options
 */
export function setCookie(
  name: string,
  value: string,
  options: Partial<Omit<CookieConfig, 'name'>> = {}
): void {
  const {
    path = '/',
    domain,
    maxAge = 3600,
    secure = true,
    sameSite = 'strict',
    httpOnly = false
  } = options;
  
  const cookieValue = encodeURIComponent(value);
  const expires = new Date(Date.now() + maxAge * 1000).toUTCString();
  
  let cookieString = `${name}=${cookieValue}; path=${path}; expires=${expires}; SameSite=${sameSite}`;
  
  if (secure) {
    cookieString += '; Secure';
  }
  
  // NOTE: HttpOnly cookies cannot be set via JavaScript/document.cookie.
  // The httpOnly flag is intentionally NOT appended here — the browser silently ignores it
  // when set from JS, creating a false sense of security. True HttpOnly cookies must be
  // set by the server via Set-Cookie response headers.
  if (httpOnly && process.env.NODE_ENV === 'development') {
    console.warn(
      `[secureTokenService] setCookie('${name}') called with httpOnly:true, ` +
      'but document.cookie cannot set HttpOnly cookies. Use the /api/auth/set-cookie ' +
      'backend endpoint for true HttpOnly storage.'
    );
  }
  
  if (domain) {
    cookieString += `; Domain=${domain}`;
  }
  
  // Set the cookie (always non-HttpOnly from JS — by design)
  document.cookie = cookieString;
}

/**
 * Get a cookie value by name
 * @param name - Cookie name
 * @returns Cookie value or null if not found
 */
export function getCookie(name: string): string | null {
  const cookies = document.cookie.split(';');
  
  for (const cookie of cookies) {
    const [cookieName, cookieValue] = cookie.trim().split('=');
    if (cookieName === name) {
      return decodeURIComponent(cookieValue);
    }
  }
  
  return null;
}

/**
 * Delete a cookie by name
 * @param name - Cookie name
 * @param options - Cookie options
 */
export function deleteCookie(
  name: string,
  options: Partial<Pick<CookieConfig, 'path' | 'domain'>> = { path: '/' }
): void {
  const { path = '/', domain } = options;
  
  let cookieString = `${name}=; path=${path}; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=strict`;
  
  if (domain) {
    cookieString += `; Domain=${domain}`;
  }
  
  document.cookie = cookieString;
}

/**
 * Store authentication token securely
 * @param token - Authentication token
 * @param config - Token storage configuration
 */
export function storeAuthToken(
  token: string,
  config: TokenStorageConfig = DEFAULT_TOKEN_STORAGE_CONFIG
): void {
  const { authTokenCookie, useSecureStorage, useLocalStorageFallback } = config;
  
  if (useSecureStorage) {
    // Store token in HttpOnly cookie via API call
    // This requires a backend endpoint to set the HttpOnly cookie
    // Request backend to set a true HttpOnly cookie via Set-Cookie response header.
    // This is the only correct way to create HttpOnly cookies from the browser.
    // Fire-and-forget: localStorage fallback below ensures auth still works if this fails.
    fetch('/api/auth/set-cookie', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ token }),
      credentials: 'include'
    }).catch(error => {
      console.warn('[secureTokenService] Backend HttpOnly cookie endpoint unavailable:', error);
    });
    
    // Always store in localStorage as the working auth source.
    // This is non-HttpOnly (accessible to JS) but is currently required for the app to function.
    // Tokens are protected against network interception via HTTPS (Secure flag on cookies).
    if (useLocalStorageFallback) {
      try {
        localStorage.setItem('astrapeak_auth_token', token);
      } catch (error) {
        console.error('Failed to store auth token in localStorage:', error);
      }
    }
  } else {
    // If secure storage is disabled, store in localStorage
    try {
      localStorage.setItem('astrapeak_auth_token', token);
    } catch (error) {
      console.error('Failed to store auth token in localStorage:', error);
      
      // Fallback to non-HttpOnly cookie
      setCookie(authTokenCookie.name, token, {
        ...authTokenCookie,
        httpOnly: false
      });
    }
  }
}

/**
 * Store refresh token securely
 * @param token - Refresh token
 * @param config - Token storage configuration
 */
export function storeRefreshToken(
  token: string,
  config: TokenStorageConfig = DEFAULT_TOKEN_STORAGE_CONFIG
): void {
  const { refreshTokenCookie, useSecureStorage, useLocalStorageFallback } = config;
  
  if (useSecureStorage) {
    // Store token in HttpOnly cookie via API call
    fetch('/api/auth/set-refresh-cookie', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ token }),
      credentials: 'include'
    }).catch(error => {
      console.error('Failed to store refresh token in HttpOnly cookie:', error);
      
      // Fallback to non-HttpOnly cookie if API call fails
      if (useLocalStorageFallback) {
        setCookie(refreshTokenCookie.name, token, {
          ...refreshTokenCookie,
          httpOnly: false
        });
      }
    });
    
    // For backward compatibility, also store in localStorage if fallback is enabled
    if (useLocalStorageFallback) {
      try {
        localStorage.setItem('astrapeak_refresh_token', token);
      } catch (error) {
        console.error('Failed to store refresh token in localStorage:', error);
      }
    }
  } else {
    // If secure storage is disabled, store in localStorage
    try {
      localStorage.setItem('astrapeak_refresh_token', token);
    } catch (error) {
      console.error('Failed to store refresh token in localStorage:', error);
      
      // Fallback to non-HttpOnly cookie
      setCookie(refreshTokenCookie.name, token, {
        ...refreshTokenCookie,
        httpOnly: false
      });
    }
  }
}

/**
 * Store tenant ID
 * @param tenant_id - Tenant ID
 * @param config - Token storage configuration
 */
export function storeTenantId(
  tenant_id: string,
  config: TokenStorageConfig = DEFAULT_TOKEN_STORAGE_CONFIG
): void {
  const { tenant_idCookie, useLocalStorageFallback } = config;
  
  // Store in cookie (not HttpOnly so it's accessible to JavaScript)
  setCookie(tenant_idCookie.name, tenant_id, tenant_idCookie);
  
  // For backward compatibility, also store in localStorage
  if (useLocalStorageFallback) {
    try {
      localStorage.setItem('tenant_id', tenant_id);
    } catch (error) {
      console.error('Failed to store tenant ID in localStorage:', error);
    }
  }
}

/**
 * Store user data
 * @param userData - User data object
 * @param config - Token storage configuration
 */
export function storeUserData(
  userData: Record<string, any>,
  config: TokenStorageConfig = DEFAULT_TOKEN_STORAGE_CONFIG
): void {
  const { userDataCookie, useLocalStorageFallback } = config;
  
  // Serialize user data
  const serializedData = JSON.stringify(userData);
  
  // Store in cookie (not HttpOnly so it's accessible to JavaScript)
  setCookie(userDataCookie.name, serializedData, userDataCookie);
  
  // For backward compatibility, also store in localStorage
  if (useLocalStorageFallback) {
    try {
      localStorage.setItem('user', serializedData);
    } catch (error) {
      console.error('Failed to store user data in localStorage:', error);
    }
  }
}

/**
 * Get authentication token
 * @param config - Token storage configuration
 * @returns Authentication token or null if not found
 */
export function getAuthToken(
  config: TokenStorageConfig = DEFAULT_TOKEN_STORAGE_CONFIG
): string | null {
  const { authTokenCookie, useSecureStorage, useLocalStorageFallback } = config;
  
  // Try to get from cookie first (for non-HttpOnly cookies)
  const cookieToken = getCookie(authTokenCookie.name);
  if (cookieToken) {
    return cookieToken;
  }
  
  // If secure storage is enabled, the token should be in an HttpOnly cookie
  // which is automatically sent with requests but not accessible via JavaScript
  
  // For backward compatibility, try localStorage
  if (useLocalStorageFallback) {
    try {
      const localStorageToken = localStorage.getItem('astrapeak_auth_token');
      if (localStorageToken) {
        return localStorageToken;
      }
    } catch (error) {
      console.error('Failed to get auth token from localStorage:', error);
    }
  }
  
  return null;
}

/**
 * Get refresh token
 * @param config - Token storage configuration
 * @returns Refresh token or null if not found
 */
export function getRefreshToken(
  config: TokenStorageConfig = DEFAULT_TOKEN_STORAGE_CONFIG
): string | null {
  const { refreshTokenCookie, useLocalStorageFallback } = config;
  
  // Try to get from cookie first (for non-HttpOnly cookies)
  const cookieToken = getCookie(refreshTokenCookie.name);
  if (cookieToken) {
    return cookieToken;
  }
  
  // If secure storage is enabled, the token should be in an HttpOnly cookie
  // which is automatically sent with requests but not accessible via JavaScript
  
  // For backward compatibility, try localStorage
  if (useLocalStorageFallback) {
    try {
      const localStorageToken = localStorage.getItem('astrapeak_refresh_token');
      if (localStorageToken) {
        return localStorageToken;
      }
    } catch (error) {
      console.error('Failed to get refresh token from localStorage:', error);
    }
  }
  
  return null;
}

/**
 * Get tenant ID
 * @param config - Token storage configuration
 * @returns Tenant ID or null if not found
 */
export function getTenantId(
  config: TokenStorageConfig = DEFAULT_TOKEN_STORAGE_CONFIG
): string | null {
  const { tenant_idCookie, useLocalStorageFallback } = config;
  
  // Try to get from cookie first
  const cookieTenantId = getCookie(tenant_idCookie.name);
  if (cookieTenantId) {
    return cookieTenantId;
  }
  
  // For backward compatibility, try localStorage
  if (useLocalStorageFallback) {
    try {
      const localStorageTenantId = localStorage.getItem('tenant_id');
      if (localStorageTenantId) {
        return localStorageTenantId;
      }
    } catch (error) {
      console.error('Failed to get tenant ID from localStorage:', error);
    }
  }
  
  return null;
}

/**
 * Get user data
 * @param config - Token storage configuration
 * @returns User data object or null if not found
 */
export function getUserData(
  config: TokenStorageConfig = DEFAULT_TOKEN_STORAGE_CONFIG
): Record<string, any> | null {
  const { userDataCookie, useLocalStorageFallback } = config;
  
  // Try to get from cookie first
  const cookieUserData = getCookie(userDataCookie.name);
  if (cookieUserData) {
    try {
      return JSON.parse(cookieUserData);
    } catch (error) {
      console.error('Failed to parse user data from cookie:', error);
    }
  }
  
  // For backward compatibility, try localStorage
  if (useLocalStorageFallback) {
    try {
      const localStorageUserData = localStorage.getItem('user');
      if (localStorageUserData) {
        return JSON.parse(localStorageUserData);
      }
    } catch (error) {
      console.error('Failed to get user data from localStorage:', error);
    }
  }
  
  return null;
}

/**
 * Clear all authentication data
 * @param config - Token storage configuration
 */
export function clearAuthData(
  config: TokenStorageConfig = DEFAULT_TOKEN_STORAGE_CONFIG
): void {
  const { authTokenCookie, refreshTokenCookie, tenant_idCookie, userDataCookie, useLocalStorageFallback } = config;
  
  // Clear cookies
  deleteCookie(authTokenCookie.name, { path: authTokenCookie.path, domain: authTokenCookie.domain });
  deleteCookie(refreshTokenCookie.name, { path: refreshTokenCookie.path, domain: refreshTokenCookie.domain });
  deleteCookie(tenant_idCookie.name, { path: tenant_idCookie.path, domain: tenant_idCookie.domain });
  deleteCookie(userDataCookie.name, { path: userDataCookie.path, domain: userDataCookie.domain });
  
  // Clear HttpOnly cookies via API call
  fetch('/api/auth/clear-cookies', {
    method: 'POST',
    credentials: 'include'
  }).catch(error => {
    console.error('Failed to clear HttpOnly cookies:', error);
  });
  
  // For backward compatibility, also clear localStorage
  if (useLocalStorageFallback) {
    try {
      localStorage.removeItem('astrapeak_auth_token');
      localStorage.removeItem('astrapeak_refresh_token');
      localStorage.removeItem('tenant_id');
      localStorage.removeItem('user');
    } catch (error) {
      console.error('Failed to clear auth data from localStorage:', error);
    }
  }
}

/**
 * Check if authentication token exists
 * @param config - Token storage configuration
 * @returns Boolean indicating if auth token exists
 */
export function hasAuthToken(
  config: TokenStorageConfig = DEFAULT_TOKEN_STORAGE_CONFIG
): boolean {
  return getAuthToken(config) !== null;
}

/**
 * Secure Token Service
 */
export const secureTokenService = {
  storeAuthToken,
  storeRefreshToken,
  storeTenantId,
  storeUserData,
  getAuthToken,
  getRefreshToken,
  getTenantId,
  getUserData,
  clearAuthData,
  hasAuthToken,
  setCookie,
  getCookie,
  deleteCookie
};

export default secureTokenService;
